import React from 'react';

import {
  Card,
  MessageFeed,
  Input,
  Button,
  Spinner,
  EmptyState,
  Notification,
} from '@ergeon/core-components';
import { PaperAirplaneIcon } from '@ergeon/icons/hero/micro';
import { SparklesIcon } from '@ergeon/icons/hero/outline';

import { useAIAgentContext } from './AIAgentContext';

const PLACEHOLDER = 'Ask AI agent about customer details, order history, or get assistance...';
const BUTTON_TEXT = 'Ask AI';
const BUTTON_LOADING_TEXT = 'Processing...';

const AIAgent = () => {
  const {
    text,
    setText,
    messages,
    isSubmitting,
    isLoading,
    error,
    isDisabled,
    scrollContainerRef,
    startNewConversation,
    handleSubmit,
  } = useAIAgentContext();

  if (isLoading) {
    return (
      <Card className="flex flex-col flex-grow gap-3 p-4 w-full overflow-hidden" flavor="base" zIndex="1">
        <Card
          className="flex flex-col flex-1 justify-center items-center px-4 overflow-hidden"
          flavor="regular">
          <Spinner active size={32} />
        </Card>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col flex-grow gap-3 p-4 w-full overflow-hidden" flavor="base" zIndex="1">
      <Card
        className="flex flex-col flex-1 justify-center px-4 overflow-hidden"
        flavor="regular"
        ref={scrollContainerRef}>
        {messages.length === 0 ? (
          <EmptyState
            description="Ask me anything about customers, orders, activities, or get help with your daily tasks."
            icon={<SparklesIcon />}
            title="AI Assistant Ready" />
        ) : (
          <MessageFeed isLoading={isSubmitting} messages={messages} />
        )}
      </Card>

      {error && (
        <Notification mode="embed" type="Error">
          <div className="flex justify-between items-center gap-3">
            <span>{error}</span>
            <Button flavor="danger" onClick={startNewConversation} size="small" taste="line">
              Start Over
            </Button>
          </div>
        </Notification>
      )}

      <div className="relative flex flex-col gap-3 w-full shrink-0">
        <Input
          className="flex-1 [&_textarea]:scrollbar-hide [&_textarea]:[scrollbar-width:none] [&_textarea]:[&::-webkit-scrollbar]:[display:none]"
          disabled={isSubmitting}
          isMultiline
          multilineRows={3}
          onChange={(_event: unknown, _name: unknown, value: unknown) => setText((value as string) || '')}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder={PLACEHOLDER}
          value={text} />

        <Button
          className="right-2 bottom-2 absolute" disabled={isDisabled} flavor="primary"
          onClick={handleSubmit}>
          {isSubmitting ? (
            <>
              <Spinner active color="white" size={18} />
              {BUTTON_LOADING_TEXT}
            </>
          ) : (
            <>
              <PaperAirplaneIcon className="size-5 -rotate-45 shrink-0" />
              {BUTTON_TEXT}
            </>
          )}
        </Button>
      </div>
    </Card>
  );
};

export default AIAgent;
